// What? A card component for displaying document type options
// Why? To provide a visually appealing, clickable card for users to select document types
// How? Uses JoyUI Card with gradient background and displays name, description, and version

import React from "react";
import { Box, Card, Chip, Typography } from "@mui/joy";
import { DocumentTypeResponse } from "../../types/documentTypes";
import { generateGradient } from "../../utils/colorUtils";

interface DocumentTypeCardProps {
  documentType: DocumentTypeResponse;
  onSelect: (id: number) => void;
}

const DocumentTypeCard: React.FC<DocumentTypeCardProps> = ({
  documentType,
  onSelect,
}) => {
  const gradientStyle = {
    background: generateGradient(documentType.Name),
  };

  return (
    <Card
      variant="outlined"
      sx={{
        ...gradientStyle,
        height: 240,
        cursor: "pointer",
        transition: "all 0.2s ease",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: "primary.500",
        },
      }}
      onClick={() => onSelect(documentType.DocumentTypeID)}
    >
      {/* Version chip in top-right corner */}
      <Chip
        size="sm"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 1,
          opacity: 0.9,
        }}
      >
        v{documentType.Version}
      </Chip>

      {/* Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          pt: 5,
          px: 3,
          pb: 3,
        }}
      >
        <Typography
          level="h3"
          sx={{
            mb: 1,
            color: "text.primary",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.3,
          }}
          title={documentType.Name}
        >
          {documentType.Name}
        </Typography>

        {documentType.Description && (
          <Typography
            level="body-sm"
            sx={{
              color: "text.secondary",
              lineHeight: 1.5,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 6,
              WebkitBoxOrient: "vertical",
            }}
            title={documentType.Description}
          >
            {documentType.Description}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default DocumentTypeCard;
